// Isometric material icons of the design system (components/hud/ResourceIcon.jsx), ported to plain
// DOM strings for the subset the map labels use. The design team marked them as placeholders
// ("mejorables") to be replaced with rendered sprites later.
import type { ResourceKind } from '../world/island';

interface Face {
  readonly t: string;
  readonly l: string;
  readonly r: string;
  readonly hi?: string;
}

interface IsoBox extends Face {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly w: number;
  readonly d: number;
  readonly h: number;
}

type Projected = readonly [number, number];

function isoBoxes(boxes: readonly IsoBox[], size: number): string {
  const pr = (x: number, y: number, z: number): Projected => [(x - y) * 0.866, (x + y) * 0.5 - z];
  const faces: [Projected[], string][] = [];
  [...boxes]
    .sort((a, b) => a.x + a.y + a.z * 0.5 - (b.x + b.y + b.z * 0.5))
    .forEach((b) => {
      const { x, y, z, w, d, h } = b;
      faces.push([[pr(x, y + d, z), pr(x + w, y + d, z), pr(x + w, y + d, z + h), pr(x, y + d, z + h)], b.l]);
      faces.push([[pr(x + w, y, z), pr(x + w, y + d, z), pr(x + w, y + d, z + h), pr(x + w, y, z + h)], b.r]);
      faces.push([[pr(x, y, z + h), pr(x + w, y, z + h), pr(x + w, y + d, z + h), pr(x, y + d, z + h)], b.t]);
      if (b.hi) faces.push([[pr(x, y, z + h), pr(x + w, y, z + h), pr(x + w, y + d * 0.12, z + h), pr(x, y + d * 0.12, z + h)], b.hi]);
    });
  const all = faces.flatMap((f) => f[0]);
  const xs = all.map((p) => p[0]);
  const ys = all.map((p) => p[1]);
  const mnx = Math.min(...xs);
  const mxx = Math.max(...xs);
  const mny = Math.min(...ys);
  const mxy = Math.max(...ys);
  const k = (size * 0.96) / Math.max(mxx - mnx, mxy - mny);
  const ox = (size - (mxx - mnx) * k) / 2;
  const oy = (size - (mxy - mny) * k) / 2;
  return faces
    .map(([pts, bg]) => {
      const poly = pts.map((p) => `${((p[0] - mnx) * k + ox).toFixed(1)}px ${((p[1] - mny) * k + oy).toFixed(1)}px`).join(',');
      return `<span style="position:absolute;inset:0;background:${bg};clip-path:polygon(${poly})"></span>`;
    })
    .join('');
}

const IRON: Face = {
  t: 'linear-gradient(120deg,#e3e9f1 0%,#ffffff 32%,#c4ceda 58%,#a3afbe 100%)',
  l: 'linear-gradient(180deg,#9aa7b7,#7b8798)',
  r: 'linear-gradient(180deg,#7c889a,#5d6878)',
  hi: 'rgba(255,255,255,.75)',
};

function plates(size: number): string {
  return isoBoxes(
    [0, 1, 2].map((i) => ({ x: [0, 0.5, 0.1][i] ?? 0, y: [0, 0.3, 0.7][i] ?? 0, z: i * 1.55, w: 10, d: 7, h: 1.25, ...IRON })),
    size,
  );
}

function chip(size: number): string {
  const pcb: Face = { t: 'linear-gradient(135deg,#6fd59a,#2f9f62 60%,#24854f)', l: '#1f7445', r: '#175a35' };
  const die: Face = { t: 'linear-gradient(135deg,#4a5566,#1d232c)', l: '#13181f', r: '#0b0e13', hi: 'rgba(255,255,255,.18)' };
  const gold: Face = { t: 'linear-gradient(135deg,#fff0b0,#e7b746)', l: '#c9952c', r: '#a5761a' };
  const boxes: IsoBox[] = [{ x: 0, y: 0, z: 0, w: 10, d: 10, h: 0.9, ...pcb }];
  for (let i = 0; i < 4; i++) {
    boxes.push({ x: 1.6 + i * 2, y: 10, z: 0.1, w: 0.9, d: 0.9, h: 0.5, ...gold });
    boxes.push({ x: 10, y: 1.6 + i * 2, z: 0.1, w: 0.9, d: 0.9, h: 0.5, ...gold });
  }
  boxes.push({ x: 1.2, y: 1.2, z: 0.9, w: 1.4, d: 1.4, h: 0.15, ...gold });
  boxes.push({ x: 3, y: 3, z: 0.9, w: 4.2, d: 4.2, h: 1.3, ...die });
  return isoBoxes(boxes, size);
}

function ore(size: number): string {
  const rock: Face = { t: 'linear-gradient(135deg,#b8bec8,#7d8592)', l: '#646b77', r: '#4c525c' };
  const vein: Face = { t: 'linear-gradient(135deg,#ffcfa8,#d9814a)', l: '#b5652f', r: '#8f4c1f' };
  return isoBoxes(
    [
      { x: 0, y: 0, z: 0, w: 8, d: 7, h: 5, ...rock },
      { x: 5, y: 5, z: 0, w: 5, d: 5, h: 3.4, ...rock },
      { x: 2, y: 2, z: 5, w: 3, d: 2.5, h: 1, ...vein },
      { x: 7, y: 6.5, z: 3.4, w: 2, d: 2, h: 0.8, ...vein },
    ],
    size,
  );
}

function gear(size: number): string {
  const metal: Face = { t: 'linear-gradient(135deg,#f4f7fb,#a7b3c2)', l: '#7b8798', r: '#5d6878' };
  const boxes: IsoBox[] = [{ x: 1, y: 1, z: 0, w: 8, d: 8, h: 1.6, ...metal }];
  for (const [x, y] of [
    [4, -1],
    [4, 9],
    [-1, 4],
    [9, 4],
  ] as const) {
    boxes.push({ x, y, z: 0, w: 2, d: 2, h: 1.6, ...metal });
  }
  boxes.push({ x: 3.8, y: 3.8, z: 1.6, w: 2.4, d: 2.4, h: 0.4, t: '#4c5563', l: '#3a414b', r: '#2b3038' });
  return isoBoxes(boxes, size);
}

function flask(size: number): string {
  const S = size;
  const bd = S * 0.7;
  const nw = S * 0.24;
  const nh = S * 0.34;
  const bt = S - bd;
  const cx = S / 2;
  const bw = Math.max(1, S * 0.035);
  return (
    `<span style="position:absolute;left:${cx - nw / 2}px;top:${bt - nh + S * 0.12}px;width:${nw}px;height:${nh}px;background:linear-gradient(90deg,rgba(255,255,255,.55),rgba(210,228,255,.25) 40%,rgba(255,255,255,.45));border:${bw}px solid rgba(255,255,255,.85);border-bottom:0;box-sizing:border-box"></span>` +
    `<span style="position:absolute;left:${cx - nw * 0.7}px;top:${bt - nh + S * 0.06}px;width:${nw * 1.4}px;height:${S * 0.09}px;border-radius:${S * 0.03}px;background:linear-gradient(180deg,#b9875a,#7d5232)"></span>` +
    `<span style="position:absolute;left:${cx - bd / 2}px;top:${bt}px;width:${bd}px;height:${bd}px;border-radius:50%;overflow:hidden;background:radial-gradient(circle at 35% 30%,rgba(255,255,255,.6),rgba(205,225,255,.25) 50%,rgba(170,195,240,.35));box-shadow:inset 0 0 0 ${bw}px rgba(255,255,255,.85)">` +
    `<span style="position:absolute;left:0;right:0;bottom:0;height:58%;background:radial-gradient(120% 90% at 40% 20%,#d9b6ff,#a46ce8 45%,#6b33b8)"></span>` +
    `<span style="position:absolute;left:4%;right:4%;top:36%;height:12%;border-radius:50%;background:rgba(240,225,255,.85)"></span>` +
    `<span style="position:absolute;left:16%;top:12%;width:26%;height:16%;border-radius:50%;background:rgba(255,255,255,.75);transform:rotate(-35deg)"></span></span>`
  );
}

function drop(size: number): string {
  const S = size;
  const w = S * 0.66;
  return (
    `<span style="position:absolute;left:${(S - w) / 2}px;top:${S * 0.22}px;width:${w}px;height:${w}px;border-radius:0 50% 50% 50%;transform:rotate(45deg);background:radial-gradient(circle at 70% 70%,#cfe8ff 0%,#5aa8ff 45%,#1f66f2 100%);box-shadow:inset 0 0 0 ${Math.max(1, S * 0.03)}px rgba(255,255,255,.6)"></span>` +
    `<span style="position:absolute;left:${S * 0.36}px;top:${S * 0.48}px;width:${S * 0.12}px;height:${S * 0.2}px;border-radius:50%;background:rgba(255,255,255,.8);transform:rotate(20deg)"></span>`
  );
}

const BUILD: Record<ResourceKind, (size: number) => string> = {
  mineral: ore,
  hierro: plates,
  circuito: chip,
  ciencia: flask,
  agua: drop,
  engranaje: gear,
};

export function resourceIcon(kind: ResourceKind, size: number): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = 'resource-icon';
  el.setAttribute('aria-hidden', 'true');
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.filter = `drop-shadow(0 ${size * 0.05}px ${size * 0.05}px rgba(4,16,52,.45))`;
  el.innerHTML = BUILD[kind](size);
  return el;
}
